import type { FocusEvent, MouseEvent } from "react";
import { memo, useRef, useState } from "react";
import clsx from "clsx";

import {
  ActionInputIcon,
  FormInputBox,
  InnerInput,
  InputBGIcon,
  LoadingInputIcon,
} from "../formControl";
import mainClasses from "../styles.module.scss";

import type { IAutoCompleteProps } from "./types";
import localClasses from "./styles.module.scss";

import { Typography } from "@/components/atoms/typography";
import { useReadOnly } from "@/hooks";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";
import { Icon } from "@/components/atoms/icons";
import { EmptyOption } from "@/interfaces";

export const AutoComplete = memo(function AutoComplete(
  props: IAutoCompleteProps,
) {
  const { disabled: propsDisabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const disabled = propsDisabled || rest.isLoading;

  return (
    <InputPopover ariaRole={"listbox"} disabled={disabled || readOnly}>
      <AutocompleteInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

export function AutocompleteInput(props: IAutoCompleteProps) {
  const {
    options,
    value = { key: "", value: "" },
    onChange,
    readOnly,
    disabled,
    onFocus,
    isLoading,
    isError,
    onBlur,
    validation,
    // TODO: remove this props. It causes lagging.
    onInputChange = () => {},
    placeholder,
    onMouseDown,
    ...rest
  } = props;

  const { open, setOpen, activeIndex, refs, getReferenceProps } =
    useInputPopoverContext();

  const [inputValue, setInputValue] = useState("");
  const [_isInputIsFocused, setIsInputIsFocused] = useState(false);
  const isInputIsFocused = _isInputIsFocused && !readOnly;
  const inputRef = useRef<HTMLInputElement>(null);

  const items = options?.filter((item) =>
    item?.value?.toLowerCase()?.includes(inputValue?.toLowerCase()),
  );

  // is there any selected value or input is empty
  const hasSelectedValue = value.key !== "";

  // can user clear or modify the value or not
  const canClearItem =
    hasSelectedValue &&
    !readOnly &&
    !disabled &&
    !isLoading &&
    !isError &&
    !isInputIsFocused;

  const getInputPlaceHolder = () => {
    if (hasSelectedValue) return isInputIsFocused ? value.value : undefined;
    else return placeholder;
  };

  const inputPlaceholder = getInputPlaceHolder();

  const focusOnInput = () => inputRef.current?.focus();

  function handleItemSelection() {
    if (activeIndex === null) return;
    const activeItem = items[activeIndex] || EmptyOption;

    onChange?.(activeItem);
    setInputValue(activeItem.value);
    setOpen(false);
  }

  function inputOnChange(innerValue: string) {
    setInputValue(innerValue);
    onInputChange(innerValue);
  }

  function inputOnFocus(event: FocusEvent<HTMLInputElement, Element>) {
    setOpen(true);
    setIsInputIsFocused(true);
    onFocus?.(event);
  }
  function inputOnBlur(event: FocusEvent<HTMLInputElement, Element>) {
    setInputValue("");
    setIsInputIsFocused(false);
    setOpen(false);
    onBlur?.(event);
  }
  function inputOnMouseDown(
    event: MouseEvent<HTMLInputElement, globalThis.MouseEvent>,
  ) {
    event.stopPropagation();
    setOpen(true);
    onMouseDown?.(event);
  }

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={Boolean(disabled)}
        readOnly={Boolean(readOnly)}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
        }}
      >
        <div className={clsx(["position-relative", "w-100", "d-flex"])}>
          {!isInputIsFocused && hasSelectedValue && (
            <div
              style={{ maxWidth: `${inputRef.current?.clientWidth}px` }}
              className={clsx([mainClasses.chip, localClasses.chip, "ms-xss"])}
            >
              <Typography
                htmlAttribute={{ title: value.value }}
                createSpan
                ellipsis
              >
                {value.value}
              </Typography>
            </div>
          )}

          <InnerInput
            {...getReferenceProps({
              "aria-autocomplete": "list",
              onKeyDown(event) {
                if (event.key === "Enter") {
                  // prevent form submitting
                  event.preventDefault();

                  handleItemSelection();
                } else if (event.key.length === 1 && !open) {
                  // length of key===1 means that user has type something not just hi ALT or CONTROL or SHIFT keys
                  setOpen(true);
                }
              },
            })}
            className={clsx([mainClasses.input, "w-100"])}
            readOnly={readOnly}
            disabled={disabled}
            ref={inputRef}
            value={inputValue}
            autoComplete={"off"}
            placeholder={inputPlaceholder}
            onChange={inputOnChange}
            onFocus={inputOnFocus}
            onBlur={inputOnBlur}
            onMouseDown={inputOnMouseDown}
            {...rest}
          />
        </div>
        <LoadingInputIcon isError={isError} isLoading={isLoading}>
          {!canClearItem && (
            <InputBGIcon disabled={disabled} readOnly={readOnly}>
              <Icon name={"Magnifer"} />
            </InputBGIcon>
          )}
        </LoadingInputIcon>
        {canClearItem && (
          <ActionInputIcon
            onClick={() => {
              if (!canClearItem) return;
              onChange?.({ key: "", value: "" });
            }}
          >
            <Icon name={"CloseCircle"} />
          </ActionInputIcon>
        )}
      </FormInputBox>
      <InputPopoverContent>
        {items.map((item, index) => {
          return (
            <InputMenuItemPopover
              key={item.key}
              index={index}
              onClick={handleItemSelection}
              onMouseDown={
                // cause we don't want to lose input's focus on icon clicking event
                (event) => {
                  event.preventDefault();
                }
              }
            >
              {item.value}
            </InputMenuItemPopover>
          );
        })}
      </InputPopoverContent>
    </>
  );
}
